/**
 * DataLookupField — Self-contained type-ahead lookup for any data file endpoint.
 * Manages its own search state internally so parent re-renders don't unmount it.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Search } from 'lucide-react';
import api from '../services/api';

export default function DataLookupField({ label, value, onChange, apiEndpoint, dataTestId }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const blurRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  const doSearch = async (q) => {
    try {
      const res = await api.get(apiEndpoint, { params: { search: q || '', page_size: 10 } });
      const d = res.data;
      // Handle different API response shapes: {data:[...]}, {job_titles:[...]}, [...], etc.
      const items = Array.isArray(d) ? d : d.data || d.job_titles || d.roles || d.groups || d.locations || d.uom || [];
      setResults(Array.isArray(items) ? items.slice(0, 10) : []);
    } catch { setResults([]); }
  };

  const handleFocus = () => { doSearch(query || ''); setOpen(true); };
  const handleBlur = () => { blurRef.current = setTimeout(() => setOpen(false), 300); };

  return (
    <div className="relative">
      {label && <Label className="text-xs mb-1 block">{label}</Label>}
      <div className="relative">
        <Input value={query}
          onChange={e => { setQuery(e.target.value); onChange(e.target.value); doSearch(e.target.value); setOpen(true); }}
          onFocus={handleFocus} onBlur={handleBlur}
          className="pr-8" data-testid={dataTestId} />
        <Search className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-[200] top-full left-0 right-0 mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto"
          onMouseDown={e => e.preventDefault()}>
          {results.map((s, i) => (
            <div key={i} className="w-full text-left px-3 py-2 text-sm hover:bg-accent border-b last:border-b-0 cursor-pointer"
              onMouseDown={e => { e.preventDefault(); e.stopPropagation(); const code = s.code || s.value || s; setQuery(code); onChange(code); setOpen(false); }}>
              <span className="font-mono text-primary text-xs font-medium">{s.code || s.value || s}</span>
              {(s.description || s.title || s.name) && <span className="text-muted-foreground text-xs ml-2">— {s.description || s.title || s.name}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
